import type {
  CashierPaymentMethod,
  CurrencyPrimitive,
  SavedPaymentAccountSummary,
} from "@/api/cashier";
import type { SelectMenuItemType } from "@/pages/withdrawals/choose-method/payment-method-selector-card/types";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { useTranslation } from "react-i18next";
import { moneyToMajorUnits } from "@/utils/helpers";

export const usePaymentMethodSelectorItemMappers = (
  tradingCurrency?: CurrencyPrimitive
) => {
  const { t } = useTranslation();
  const { formatNumber } = useFormatAmount();

  const isExpiryLike = (value: string) => /^\d{2}\/\d{2}$/.test(value.trim());

  const convertSavedPaymentAccountSummaryToOptionItem = (
    item: SavedPaymentAccountSummary
  ) => {
    const secondaryDetail = item.secondary_detail
      ? isExpiryLike(item.secondary_detail)
        ? t("deposit.methods.saved_methods.expiration", {
            expiryDate: item.secondary_detail,
          })
        : item.secondary_detail
      : "";

    return {
      id: item.id,
      title: item.detail,
      detail: secondaryDetail,
      logo_url: item.logo_url,
      type: "saved",
      data: item,
    } as SelectMenuItemType;
  };

  const convertNewPaymentMethodToOptionItem = (item: CashierPaymentMethod) => {
    const processingTime =
      "deposit" in item.base_settings
        ? item.base_settings.deposit.expected_processing_time
        : item.base_settings.payout.expected_processing_time;

    let limitMessage = "";
    if (tradingCurrency) {
      const limitSettings = item.settings[tradingCurrency.code]?.limit;
      if (limitSettings && !limitSettings.min.unlimited) {
        limitMessage = t("deposit.methods.properties.limit_minimum", {
          value: formatNumber(
            moneyToMajorUnits(limitSettings.min.value),
            tradingCurrency
          ),
          currency: tradingCurrency.code,
        });
      }
    }

    const detail = [processingTime, limitMessage].filter(Boolean).join(" / ");

    return {
      id: item.id,
      title: item.display_name,
      detail,
      logo_url: item.logo_url,
      type: "new",
      data: item,
    } as SelectMenuItemType;
  };

  return {
    convertSavedPaymentAccountSummaryToOptionItem,
    convertNewPaymentMethodToOptionItem,
  };
};
